// ─────────────────────────────────────────────
// Oike · Mi marca — Instagram and LinkedIn pieces with identity A «Pizarra» (approved by Ale on 04-11-2026 from the
// v3 samples). Pure: shared by the editor, the PNG and PDF builders, owner-space and the tests.
//
// - One template per format, each with two compositions («A» and «B») over the SAME content: changing the composition
//   or removing the photo never drops a text. The photo is optional and adjustable (move, zoom, reset, remove); it has
//   its own zone and no text or stroke ever enters it.
// - One layout (layoutPizarra) draws the preview (SVG), the PNG and the PDF, measured with the identity's font files.
// - Text never shrinks to fit (titles step between a large and a minimum legible size, like the notebook cover). What
//   does not fit is drawn whole and named, with its field: it blocks approval, never cut. In the carousel and the
//   LinkedIn document a slide or page that does not fit continues on the next one; the count and the numbering are
//   computed from the layout.
// - Every size is chosen for the phone (BRAND_FORMATS.viewWidth): no text under 10.5 CSS px, titles over 16 px.
// ─────────────────────────────────────────────
import { IDENTITY_A } from './brandIdentity.js';
import * as S from './brandStrokes.js';

const C = IDENTITY_A.colors;
const ACC = [C.turquesa, C.coral, C.amarillo];
const ACC_T = [C.turquesaTexto, C.coralTexto, C.amarilloTexto];
const AUTHOR = 'Alejandra Cadario';

export const PZ_TEMPLATES = Object.freeze({
  pz_ig_post: { format: 'ig_post', size: { w: 1080, h: 1350 }, view: 390, kind: 'single', fields: ['kicker', 'title', 'highlight', 'body', 'points', 'note'] },
  pz_ig_carousel: { format: 'ig_carousel', size: { w: 1080, h: 1350 }, view: 390, kind: 'carousel' },
  pz_ig_story: { format: 'ig_story', size: { w: 1080, h: 1920 }, view: 390, kind: 'single', fields: ['kicker', 'title', 'highlight', 'points', 'note'] },
  // LinkedIn post: square for new pieces; 4:5 (li_post) only for contents of the weekly plan saved before the renewal,
  // so their format (and the keys of their tasks) never change
  pz_li_post: { format: 'li_square', size: { w: 1080, h: 1080 }, sizes: { li_post: { w: 1080, h: 1350 } }, view: 360, kind: 'single', fields: ['kicker', 'title', 'highlight', 'body', 'points'] },
  pz_li_document: { format: 'li_document', size: { w: 1080, h: 1350 }, view: 360, kind: 'document' },
  // YouTube thumbnail: seen very small in lists (about 320 px wide); the duration covers the bottom right corner
  pz_youtube: { format: 'youtube', size: { w: 1280, h: 720 }, view: 320, previewWidth: 560, kind: 'single', fields: ['kicker', 'title', 'highlight'] },
  // TikTok video cover (format «tiktok_cover», added by Omni on 04-11-2026): an image, apart from the TikTok script. The
  // profile grid shows it cropped to 3:4 and very small (about 130 px): the crop is an estimate of current guides (not an
  // official TikTok specification), adjustable per piece (cropTop), shown only as a guide
  pz_tiktok_cover: { format: 'tiktok_cover', size: { w: 1080, h: 1920 }, view: 130, previewWidth: 300, kind: 'single', fields: ['title', 'highlight'], crop: { top: 240, h: 1440, min: 120, max: 360 } },
});
export const PZ_LAYOUTS = Object.freeze({
  pz_ig_post: { A: { label: 'Pregunta', hint: 'Pizarra oscura y título arriba. Sin foto, los tres puntos forman una escalera.' }, B: { label: 'Esquema claro', hint: 'Fondo claro, título centrado y los tres puntos como 1 → 2 → 3. La foto va en una franja arriba.' } },
  pz_ig_carousel: { A: { label: 'Pasos', hint: 'Portada oscura centrada; láminas claras con un número grande.' }, B: { label: 'Dos tonos', hint: 'Portada mitad pizarra y mitad papel; láminas con una columna para el número.' } },
  pz_ig_story: { A: { label: 'Lista para responder', hint: 'Lista para marcar y flecha a la caja de respuesta. La foto va junto al título.' }, B: { label: 'Pregunta grande', hint: 'La pregunta al centro sobre un bloque coral. La foto va arriba.' } },
  pz_li_post: { A: { label: 'Columna', hint: 'La foto en una franja a la izquierda; título, texto y esquema en el resto.' }, B: { label: 'Titular', hint: 'Banda oscura con el título; debajo, texto y puntos numerados. La foto va en un recuadro.' } },
  pz_li_document: { A: { label: 'Guía', hint: 'Todas las páginas claras; lectura y preguntas en una columna.' }, B: { label: 'Ficha', hint: 'Portada oscura; páginas con una columna al margen para el número de sección.' } },
  pz_youtube: { A: { label: 'Pizarra con foto', hint: 'Fondo oscuro, título enorme a la izquierda y la foto en la franja derecha. Sin foto, una escalera que sube.' }, B: { label: 'Pizarra clara', hint: 'Fondo claro, título centrado con subrayado coral y pasos 1 → 2 → 3. La foto va a la izquierda.' } },
  pz_tiktok_cover: { A: { label: 'Pregunta grande', hint: 'Pizarra oscura y título enorme dentro del recorte del perfil; la foto debajo. Sin foto, tres círculos 1 → 2 → 3.' }, B: { label: 'Nota clara', hint: 'Fondo claro, foto arriba y título debajo con un óvalo coral. Sin foto, el título queda en el centro del recorte.' } },
});
export const isPizarraTemplate = (key) => !!PZ_TEMPLATES[key];

// Limits of each text (characters)
export const PZ_LIMITS = Object.freeze({
  kicker: 40, title: 90, highlight: 30, body: 240, point: 40, note: 60, caption: 2200,
  slideTitle: 70, slideBody: 600, slideNote: 50, slides: { min: 1, max: 8 },
  docTitle: 90, para: 900, paras: 6, quote: 140, q: 80, a: 300, items: 5, sections: { min: 1, max: 6 },
});
const L = PZ_LIMITS;

// ── Sample content (marked as sample: a notice, never a block) ──
const SAMPLES = {
  pz_ig_post: { kicker: 'CUADERNO DE DECISIONES', title: '¿Y si tu próximo paso es más chico?', highlight: 'chico', body: 'Cuando todo parece grande, elegí una acción que puedas empezar esta semana.', points: ['Depende de mí', 'Cabe en una semana', 'Sé cómo empezar'], note: 'Guardalo para tu próxima decisión' },
  pz_ig_story: { kicker: 'PAUSA DE UN MINUTO', title: 'Elegí un paso que quepa en esta semana', highlight: 'esta semana', points: ['Lo puedo empezar hoy', 'Depende de mí', 'Sé cómo se ve terminado'], note: 'Respondeme: ¿cuál es el tuyo?' },
  pz_youtube: { kicker: 'CUADERNO DE DECISIONES', title: 'Tu próximo paso es más chico', highlight: 'más chico' },
  pz_tiktok_cover: { title: 'Tu próximo paso es más chico', highlight: 'más chico' },
  pz_li_post: { kicker: 'IDEA PARA EQUIPOS', title: 'Menos deseos, más próximos pasos', highlight: 'próximos', body: 'Un equipo no cambia por su lista de objetivos: cambia cuando alguien empieza una acción el lunes.', points: ['deseo', 'acción', 'hábito'] },
  pz_ig_carousel: {
    cover: { kicker: 'CARRUSEL · 3 PASOS', title: 'Del cambio a la acción', highlight: 'acción', body: 'Cómo pasar de «quiero cambiar» a algo que empezás esta semana.' },
    slides: [
      { title: 'Nombrá lo que querés cambiar', body: 'Una frase, sin explicaciones. Si no entra en una línea, todavía es un tema y no un cambio.', note: 'una sola frase' },
      { title: 'Elegí una acción posible', body: 'Antes de comprometerte, revisá que dependa de vos, que quepa en una semana y que sepas cómo empezar.', note: 'si falla una, achicala' },
    ],
    closing: { title: 'Tu próximo paso no tiene que ser grande', highlight: 'grande', body: 'Guardá este carrusel y volvé a él la próxima vez que tengas que decidir.' },
  },
  pz_li_document: {
    cover: { kicker: 'GUÍA BREVE · PDF', title: 'Cómo elegir tu próximo paso profesional', highlight: 'próximo paso', body: 'Tres preguntas para pasar de lo que querés cambiar a una acción que empieza esta semana.', note: '3 minutos' },
    sections: [
      { type: 'reading', title: 'Por qué cuesta decidir', highlight: 'decidir', paras: ['Cuando pensamos en un cambio profesional solemos mirar el final: el puesto, el proyecto, la versión de nosotros que ya lo logró. Esa distancia paraliza.', 'Un próximo paso, en cambio, se mide en días. Es lo bastante chico para empezar y lo bastante claro para saber si lo hiciste.'], quote: '«Grande» no es una medida: «esta semana» sí.' },
      { type: 'questions', title: 'Tres preguntas', highlight: 'preguntas', items: [{ q: '¿Depende de mí?', a: 'Si necesita el permiso de otra persona, el paso es pedirlo.' }, { q: '¿Cabe en una semana?', a: 'Si no, dividilo hasta que quepa.' }, { q: '¿Sé cómo empezar?', a: 'Escribí la primera acción concreta: llamar, leer, anotar.' }] },
    ],
  },
};
const sampleStrings = (() => {
  const out = new Set();
  const walk = (v) => { if (typeof v === 'string') { if (v.length > 3) out.add(v); } else if (Array.isArray(v)) v.forEach(walk); else if (v && typeof v === 'object') Object.values(v).forEach(walk); };
  walk(SAMPLES);
  return out;
})();
export const PZ_SECTION_TYPES = Object.freeze({ reading: { label: 'Lectura' }, questions: { label: 'Preguntas' } });
export const newSection = (type) => (type === 'questions' ? { type, title: '', highlight: '', items: [{ q: '', a: '' }] } : { type: 'reading', title: '', highlight: '', paras: [''], quote: '' });

/** A new piece's content: the sample texts (a notice until they are replaced), composition A, no photo. */
export function newPizarraContent(key) {
  return { identity: { key: IDENTITY_A.key, version: IDENTITY_A.version }, layout: 'A', ...structuredClone(SAMPLES[key]), image: null };
}

// ── Validation (the same in the screen and in owner-space) ──
export function pizarraInput(key, v, fail = (m) => { throw new Error(m); }) {
  const tpl = PZ_TEMPLATES[key];
  if (!tpl) fail('template');
  const c = v && typeof v === 'object' ? v : {};
  // A text longer than its field is kept whole (the layout names it and it blocks approval; never cut): only three times
  // the field is refused, so nothing unreasonable is stored
  const str = (x, max, what) => { const t = typeof x === 'string' ? x.replace(/\r/g, '') : ''; if (t.length > max * 3) fail(`too long: ${what}`); return t; };
  const image = (im) => {
    if (!im || typeof im !== 'object' || typeof im.photoId !== 'string' || !/^rec[A-Za-z0-9]{14}$/.test(im.photoId)) return null;
    const n = (x, lo, hi, d) => { const k = Number(x); return Number.isFinite(k) ? Math.max(lo, Math.min(hi, Math.round(k * 100) / 100)) : d; };
    return { photoId: im.photoId, x: n(im.x, -1, 1, 0), y: n(im.y, -1, 1, 0), zoom: n(im.zoom, 0.5, 2.5, 1) };
  };
  const out = { identity: { key: IDENTITY_A.key, version: IDENTITY_A.version }, layout: c.layout === 'B' ? 'B' : 'A', image: image(c.image) };
  // A piece of the weekly plan keeps its reference (week, content, shared fingerprint) and the fingerprint of the texts the
  // AI proposed (to tell whether Ale already edited them); nothing else of the plan lives in the piece's texts
  const plan = planRef(c.plan);
  if (plan) out.plan = plan;
  if (typeof c.aiFp === 'string' && /^[a-z0-9]{1,16}$/.test(c.aiFp)) out.aiFp = c.aiFp;
  const head = (h) => ({ kicker: str(h?.kicker, L.kicker, 'kicker'), title: str(h?.title, L.title, 'title'), highlight: str(h?.highlight, L.highlight, 'highlight'), body: str(h?.body, L.body, 'body') });
  if (tpl.kind === 'single') {
    const h = head(c);
    for (const f of tpl.fields) if (f !== 'points') out[f] = f === 'note' ? str(c.note, L.note, 'note') : h[f];
    if (tpl.fields.includes('points')) out.points = [0, 1, 2].map(i => str(c.points?.[i], L.point, 'point'));
    // TikTok cover: where the profile crop is (an adjustable estimate)
    if (tpl.crop) { const v = Math.round(Number(c.cropTop)); out.cropTop = Number.isFinite(v) ? Math.max(tpl.crop.min, Math.min(tpl.crop.max, v)) : tpl.crop.top; }
  } else if (tpl.kind === 'carousel') {
    out.cover = head(c.cover);
    const slides = Array.isArray(c.slides) ? c.slides : [];
    if (slides.length < L.slides.min || slides.length > L.slides.max) fail('invalid slides');
    out.slides = slides.map(s => ({ title: str(s?.title, L.slideTitle, 'slide title'), body: str(s?.body, L.slideBody, 'slide body'), note: str(s?.note, L.slideNote, 'slide note') }));
    out.closing = { title: str(c.closing?.title, L.slideTitle, 'closing title'), highlight: str(c.closing?.highlight, L.highlight, 'closing highlight'), body: str(c.closing?.body, L.body, 'closing body') };
  } else {
    out.cover = { ...head(c.cover), note: str(c.cover?.note, L.note, 'cover note') };
    const secs = Array.isArray(c.sections) ? c.sections : [];
    if (secs.length < L.sections.min || secs.length > L.sections.max) fail('invalid sections');
    out.sections = secs.map(s => {
      const base = { title: str(s?.title, L.docTitle, 'section title'), highlight: str(s?.highlight, L.highlight, 'section highlight') };
      if (s?.type === 'questions') {
        const items = Array.isArray(s.items) ? s.items : [];
        if (!items.length || items.length > L.items) fail('invalid questions');
        return { type: 'questions', ...base, items: items.map(it => ({ q: str(it?.q, L.q, 'question'), a: str(it?.a, L.a, 'answer') })) };
      }
      const paras = Array.isArray(s?.paras) ? s.paras : [];
      if (!paras.length || paras.length > L.paras) fail('invalid paragraphs');
      return { type: 'reading', ...base, paras: paras.map(p => str(p, L.para, 'paragraph')), quote: str(s?.quote, L.quote, 'quote') };
    });
  }
  return out;
}
const PLAN_REF = { week: /^\d{4}-\d{2}-\d{2}$/, itemKey: /^[a-z0-9-]{4,40}$/, sharedFp: /^[a-z0-9]{0,16}$/ };
function planRef(v) {
  if (!v || typeof v !== 'object') return null;
  if (!PLAN_REF.week.test(String(v.week || '')) || !PLAN_REF.itemKey.test(String(v.itemKey || '')) || !PLAN_REF.sharedFp.test(String(v.sharedFp || ''))) return null;
  return { week: v.week, itemKey: v.itemKey, sharedFp: String(v.sharedFp || '') };
}
export const pizarraPhotoIds = (content) => (content?.image?.photoId ? [content.image.photoId] : []);

/** Notices (never block): sample texts, an empty title, a highlighted word that is not in its title. */
export function pizarraWarnings(key, content) {
  const out = [];
  const texts = [];
  const walk = (v) => { if (typeof v === 'string') texts.push(v); else if (Array.isArray(v)) v.forEach(walk); else if (v && typeof v === 'object') Object.entries(v).forEach(([k, x]) => { if (k !== 'identity' && k !== 'image' && k !== 'layout' && k !== 'type') walk(x); }); };
  walk(content);
  if (texts.some(t => sampleStrings.has(t))) out.push('sample_text');
  const heads = PZ_TEMPLATES[key]?.kind === 'single' ? [content] : [content?.cover, content?.closing, ...(content?.sections || [])].filter(Boolean);
  if (!String((PZ_TEMPLATES[key]?.kind === 'single' ? content : content?.cover)?.title || '').trim()) out.push('missing:title');
  if (heads.some(h => h.highlight && !String(h.title || '').toLowerCase().includes(String(h.highlight).toLowerCase()))) out.push('highlight_missing');
  return out;
}

/** What blocks approval (it keeps the piece from being drawn correctly). The rest are notices. */
export const PZ_BLOCKING = Object.freeze(['too_long', 'overflow', 'overlap', 'ui', 'glyph', 'image_loading']);
/** Notices of the layout (they never block): a title outside the profile crop, which is only an estimate. */
export const PZ_LAYOUT_NOTICES = Object.freeze(['crop']);

/** Each text with its field and limit (the same names as the layout and the form). */
export function pizarraFields(key, c) {
  const kind = PZ_TEMPLATES[key]?.kind; const out = [];
  const add = (field, text, max) => out.push({ field, text: String(text || ''), max });
  const head = (h, p) => { add(`${p}kicker`, h?.kicker, L.kicker); add(`${p}title`, h?.title, L.title); add(`${p}highlight`, h?.highlight, L.highlight); add(`${p}body`, h?.body, L.body); };
  if (kind === 'single') { head(c, ''); (c.points || []).forEach((x, i) => add(`points.${i}`, x, L.point)); add('note', c.note, L.note); }
  else if (kind === 'carousel') {
    head(c.cover, 'cover.');
    (c.slides || []).forEach((s, i) => { add(`slides.${i}.title`, s.title, L.slideTitle); add(`slides.${i}.body`, s.body, L.slideBody); add(`slides.${i}.note`, s.note, L.slideNote); });
    add('closing.title', c.closing?.title, L.slideTitle); add('closing.highlight', c.closing?.highlight, L.highlight); add('closing.body', c.closing?.body, L.body);
  } else if (kind === 'document') {
    head(c.cover, 'cover.'); add('cover.note', c.cover?.note, L.note);
    (c.sections || []).forEach((s, i) => {
      add(`sections.${i}.title`, s.title, L.docTitle); add(`sections.${i}.highlight`, s.highlight, L.highlight);
      (s.paras || []).forEach((t, j) => add(`sections.${i}.paras.${j}`, t, L.para)); add(`sections.${i}.quote`, s.quote, L.quote);
      (s.items || []).forEach((it, j) => { add(`sections.${i}.items.${j}.q`, it.q, L.q); add(`sections.${i}.items.${j}.a`, it.a, L.a); });
    });
  }
  return out;
}
/** Texts longer than their field: named with the field, the length and the limit (never cut). */
export const pizarraTooLong = (key, c) => pizarraFields(key, c).filter(x => x.text.length > x.max).map(x => ({ code: 'too_long', field: x.field, length: x.text.length, max: x.max }));

// ── Layout ──
const wrapWith = (measure) => (text, font, size, width) => {
  const out = [];
  for (const para of String(text || '').split('\n')) {
    const words = para.split(/\s+/).filter(Boolean);
    if (!words.length) { out.push(''); continue; }
    let line = '';
    for (const w0 of words) {
      let w = w0;
      while (measure(w, font, size) > width && w.length > 1) { let k = w.length - 1; while (k > 1 && measure(w.slice(0, k), font, size) > width) k--; if (line) { out.push(line); line = ''; } out.push(w.slice(0, k)); w = w.slice(k); }
      const t = line ? `${line} ${w}` : w;
      if (measure(t, font, size) <= width) line = t; else { out.push(line); line = w; }
    }
    if (line) out.push(line);
  }
  return out;
};

/** The image inside its zone: covers it at zoom 1 (cropped), smaller at zoom < 1; x/y move it (−1…1). */
export function imageRect(zone, dims, im, bias = { x: 0, y: 0 }) {
  const base = Math.max(zone.w / dims.w, zone.h / dims.h);
  const s = base * (im.zoom || 1);
  const w = dims.w * s, h = dims.h * s;
  const mx = Math.max(-1, Math.min(1, (im.x || 0) + bias.x)), my = Math.max(-1, Math.min(1, (im.y || 0) + bias.y));
  return { x: zone.x + (zone.w - w) / 2 + mx * Math.abs(w - zone.w) / 2, y: zone.y + (zone.h - h) / 2 + my * Math.abs(h - zone.h) / 2, w, h };
}

/**
 * The pages of a piece. images: { photoId: { w, h } } (loaded photos). Returns { size, pages: [{ n, items, label,
 * bottom, zones }], issues: [{ code, field, page }] }. Items: rect, texture, path, text, image (clipped), slot.
 */
export function layoutPizarra(key, content, { measure, images = {}, format = null } = {}) {
  const tpl0 = PZ_TEMPLATES[key];
  const tpl = format && tpl0.sizes?.[format] ? { ...tpl0, size: tpl0.sizes[format] } : tpl0;
  const wrap = wrapWith(measure);
  const tw = (s, font, size) => measure(s, font, size);
  const B = content.layout === 'B';
  const W = tpl.size.w, H = tpl.size.h;
  const im = content.image?.photoId ? content.image : null;
  const dims = im ? images[im.photoId] : null;
  const issues = [];
  const pages = [];

  // ── drawing helpers on a page ──
  const page = (bg, { dark = false, bottom = H - 120, label = '' } = {}) => {
    const p = { n: pages.length + 1, label, bottom, zones: [], items: [{ t: 'rect', x: 0, y: 0, w: W, h: H, fill: bg }] };
    if (dark) p.items.push({ t: 'texture', x: 0, y: 0, w: W, h: H });
    pages.push(p);
    return p;
  };
  const rect = (p, x, y, w, h, fill) => p.items.push({ t: 'rect', x, y, w, h, fill });
  const tex = (p, x, y, w, h) => p.items.push({ t: 'texture', x, y, w, h });
  const text = (p, x, y, s, font, size, color, anchor = 'start', f = null, foot = false) => {
    const w = tw(s, font, size);
    p.items.push({ t: 'text', x: anchor === 'middle' ? x - w / 2 : anchor === 'end' ? x - w : x, y, w, text: s, font, size, color, ...(f ? { f } : {}), ...(foot ? { foot: true } : {}) });
    return w;
  };
  const strokes = (p, list, color, width, opacity = 1) => { for (const s of list) p.items.push({ t: 'path', d: s.d, stroke: color, width, opacity: s.pass ? opacity * 0.55 : opacity }); };
  const line = (p, d, color, width) => strokes(p, [{ d, pass: 0 }], color, width);
  const para = (p, s, x, y, w, font, size, lh, color, f, anchor = 'start') => {
    const ls = wrap(s, font, size, w);
    ls.forEach((l, i) => text(p, anchor === 'middle' ? x + w / 2 : x, y + i * lh, l, font, size, color, anchor, f));
    return y + (Math.max(1, ls.length) - 1) * lh;
  };
  const nLines = (s, font, size, w) => wrap(s, font, size, w).length;
  const sig = (p, x, y, color, size = 32, anchor = 'start') => text(p, x, y, AUTHOR.toUpperCase(), 'bodyHeavy', size, color, anchor, null, true);
  const foot = (p, x, y, s, color, anchor = 'start') => text(p, x, y, s, 'bodyBold', 34, color, anchor, null, true);
  /** A handwritten title: as large as it fits in maxLines between max and min; the phrase is marked. */
  const title = (p, s, { x, top, w, max, min = max, maxLines = 3, maxBottom = Infinity, color, mark = null, phrase = '', anchor = 'start', f = 'title' }) => {
    let size = max, ls = wrap(s, 'title', size, w);
    // as large as it fits: in its lines and, where the zone ends (a band), in its height
    const tooBig = () => ls.length > maxLines || top + ls.length * size * 0.98 > maxBottom;
    while (tooBig() && size > min) { size = Math.max(min, size - 4); ls = wrap(s, 'title', size, w); }
    const lh = size * 0.98;
    const lines = ls.map((l, i) => ({ l, y: top + size * 0.8 + i * lh, w: tw(l, 'title', size) }));
    const lx = (ln) => (anchor === 'middle' ? x - ln.w / 2 : x);
    const ph = String(phrase || '').trim().toLowerCase();
    if (mark && ph) {
      // The phrase inside one line; or, when the line break splits it, each part on its own line
      const segs = [];
      const one = lines.findIndex(ln => ln.l.toLowerCase().includes(ph));
      if (one >= 0) { const k = lines[one].l.toLowerCase().indexOf(ph); segs.push({ ln: lines[one], a: k, b: k + ph.length }); }
      else {
        const words = lines.flatMap((ln) => { const out = []; const re = /\S+/g; let m; while ((m = re.exec(ln.l))) out.push({ ln, a: m.index, b: m.index + m[0].length, w: m[0].toLowerCase() }); return out; });
        const pw = ph.split(/\s+/);
        for (let i = 0; i + pw.length <= words.length; i++) {
          const ok = pw.every((x, j) => (j === pw.length - 1 ? words[i + j].w.startsWith(x) : words[i + j].w === x));
          if (!ok) continue;
          const hit = words.slice(i, i + pw.length);
          hit[hit.length - 1] = { ...hit[hit.length - 1], b: hit[hit.length - 1].a + pw[pw.length - 1].length };
          for (const wd of hit) { const last = segs[segs.length - 1]; if (last && last.ln === wd.ln) last.b = wd.b; else segs.push({ ln: wd.ln, a: wd.a, b: wd.b }); }
          break;
        }
      }
      segs.forEach((sg, n) => {
        const mx = lx(sg.ln) + tw(sg.ln.l.slice(0, sg.a), 'title', size), mw = tw(sg.ln.l.slice(sg.a, sg.b), 'title', size);
        const seed = S.seedOf(`${f}:${mark.kind}:${n}`);
        if (mark.kind === 'block') p.items.push({ t: 'path', d: S.block(mx - 14, sg.ln.y - size * 0.78, mw + 28, size * 0.95, seed), fill: mark.color });
        if (mark.kind === 'underline') strokes(p, S.underline(mx - 4, sg.ln.y + size * 0.16, mx + mw + 8, sg.ln.y + size * 0.1, seed), mark.color, mark.width || size * 0.07);
        if (mark.kind === 'loop') strokes(p, S.loop(mx + mw / 2, sg.ln.y - size * 0.3, mw / 2 + size * 0.1, size * 0.5, seed), mark.color, mark.width || size * 0.05);
        if (mark.sparks && n === 0) strokes(p, S.sparks(mx - 18, sg.ln.y - size * 0.55, seed, { r: size * 0.42 }), mark.sparks, size * 0.035);
      });
    }
    for (const ln of lines) text(p, lx(ln), ln.y, ln.l, 'title', size, color, 'start', f);
    return { bottom: top + Math.max(1, lines.length) * lh, size, lines: lines.length };
  };
  /** The photo in its zone (or, while it loads, a marked space). Wide zones show the upper part by default. */
  const photo = (p, zone, dark, panel = null) => {
    p.zones.push(zone);
    if (panel) rect(p, zone.x, zone.y, zone.w, zone.h, panel);
    if (!dims?.w || !dims?.h) {
      p.items.push({ t: 'slot', x: zone.x + 10, y: zone.y + 10, w: zone.w - 20, h: zone.h - 20, dark });
      if (!issues.some(i => i.code === 'image_loading')) issues.push({ code: 'image_loading', field: 'image', page: p.n });
      return;
    }
    const wide = zone.w / zone.h > dims.w / dims.h;
    const r = imageRect(zone, dims, im, { x: 0, y: wide ? 0.6 : 0 });
    p.items.push({ t: 'image', photoId: im.photoId, ...r, clip: zone });
  };
  const check = (p, x, y, col) => line(p, `M${x - 16} ${y} L${x - 2} ${y + 16} L${x + 24} ${y - 26}`, col, 6);

  if (key === 'pz_ig_post') {
    const M = 80, c = content;
    if (!B) {
      const p = page(C.carbon, { dark: true, bottom: H - 130 });
      text(p, M, 128, c.kicker, 'bodyHeavy', 32, C.turquesa, 'start', 'kicker');
      const t = title(p, c.title, { x: M, top: 170, w: W - 2 * M, max: im ? 124 : 140, min: 96, maxLines: im ? 3 : 3, color: C.tiza, mark: { kind: 'block', color: C.turquesa, sparks: C.amarillo }, phrase: c.highlight });
      if (im) {
        const colW = 450;
        photo(p, { x: 580, y: t.bottom + 40, w: W - 580 - 30, h: H - t.bottom - 70 }, true);
        let y = para(p, c.body, M, t.bottom + 92, colW, 'body', 36, 50, C.tizaSoft, 'body') + 96;
        c.points.forEach((l, i) => { if (!l) return; line(p, S.dash(M, y - 12, 26), ACC[i], 6); y = para(p, l, M + 46, y, colW - 46, 'bodyBold', 36, 46, C.tiza, `points.${i}`) + 62; });
        para(p, c.note, M, y + 50, colW, 'note', 50, 54, C.amarillo, 'note');
      } else {
        const by = para(p, c.body, M, t.bottom + 92, 640, 'body', 38, 54, C.tizaSoft, 'body');
        para(p, c.note, M, by + 120, 380, 'note', 46, 50, C.amarillo, 'note');
        const base = H - 180, sx = 210, st = 250, sh = 150;
        line(p, `M${sx} ${base} L${sx + st} ${base} L${sx + st} ${base - sh} L${sx + 2 * st} ${base - sh} L${sx + 2 * st} ${base - 2 * sh} L${sx + 3 * st} ${base - 2 * sh}`, C.tiza, 5);
        c.points.forEach((l, i) => { const n = nLines(l, 'note', 42, st - 24); para(p, l, sx + i * st + 12, base - i * sh - 34 - (n - 1) * 42, st - 24, 'note', 42, 42, ACC[i], `points.${i}`, 'middle'); });
        strokes(p, S.arrow(sx + 3 * st - 20, base - 2 * sh - 30, sx + 3 * st + 50, base - 2 * sh - 110, 5, { bend: -10, head: 14 }), C.amarillo, 4);
      }
      sig(p, M, H - 70, C.tiza);
    } else {
      const p = page(C.papel, { bottom: H - 110 });
      let top = 140;
      if (im) { photo(p, { x: 0, y: 0, w: W, h: 450 }, false, C.panel); top = 520; }
      text(p, W / 2, top, c.kicker, 'bodyHeavy', 32, C.coralTexto, 'middle', 'kicker');
      const t = title(p, c.title, { x: W / 2, top: top + 26, w: W - 2 * M, max: im ? 96 : 132, min: im ? 80 : 96, maxLines: 3, color: C.petroleo, anchor: 'middle', mark: { kind: 'loop', color: C.coral, width: 6 }, phrase: c.highlight });
      const y = para(p, c.body, M + 50, t.bottom + (im ? 54 : 70), W - 2 * M - 100, 'body', 36, 50, C.texto, 'body', 'middle');
      const sy = im ? y + 160 : Math.max(y + 230, H - 380), gap = (W - 2 * M) / 3;
      let ly = sy + 30;
      c.points.forEach((l, i) => {
        const cx = M + gap * i + gap / 2;
        strokes(p, S.loop(cx, sy - 70, 40, 40, 500 + i), ACC[i], 5);
        text(p, cx, sy - 50, String(i + 1), 'title', 60, C.petroleo, 'middle');
        ly = Math.max(ly, para(p, l, cx - gap / 2 + 14, sy + 30, gap - 28, 'bodyBold', 34, 40, C.tinta, `points.${i}`, 'middle'));
        if (i < 2) strokes(p, S.arrow(cx + 56, sy - 72, cx + gap - 56, sy - 72, 510 + i, { bend: -10, head: 12 }), C.petroleo, 3.4);
      });
      para(p, c.note, M, im ? ly + 90 : Math.max(ly + 100, H - 150), W - 2 * M, 'note', im ? 44 : 48, 50, C.turquesaTexto, 'note', 'middle');
      sig(p, W / 2, H - 50, C.gris, 30, 'middle');
    }
  }

  if (key === 'pz_ig_story') {
    const c = content;
    if (!B) {
      const M = 80;
      const p = page(C.petroleo, { dark: true, bottom: 1575 });
      text(p, M, 330, c.kicker, 'bodyHeavy', 32, C.amarillo, 'start', 'kicker');
      let t;
      if (im) { photo(p, { x: 620, y: 280, w: W - 620 - 50, h: 470 }, true); t = title(p, c.title, { x: M, top: 370, w: 520, max: 104, min: 84, maxLines: 4, color: C.tiza, mark: { kind: 'underline', color: C.coral, width: 8 }, phrase: c.highlight }); }
      else t = title(p, c.title, { x: M, top: 370, w: W - 2 * M, max: 138, min: 104, maxLines: 3, color: C.tiza, mark: { kind: 'underline', color: C.coral, width: 9 }, phrase: c.highlight });
      let y = Math.max(t.bottom, im ? 750 : 0) + 130;
      c.points.forEach((r, i) => {
        if (!r) return;
        strokes(p, S.loop(M + 34, y - 14, 30, 30, 40 + i), ACC[i], 4.5);
        if (i === 0) check(p, M + 34, y - 16, C.tiza);
        y = para(p, r, M + 100, y, W - 2 * M - 110, 'bodyBold', 50, 62, C.tiza, `points.${i}`) + 140;
      });
      const nl = nLines(c.note, 'note', 62, W - 2 * M);
      para(p, c.note, M, 1500 - (nl - 1) * 66, W - 2 * M, 'note', 62, 66, C.amarillo, 'note');
      strokes(p, S.arrow(M + 440, 1530, M + 400, 1650, 21, { bend: -18, head: 16 }), C.amarillo, 5);
    } else {
      const M = 90;
      const p = page(C.carbon, { dark: true, bottom: 1575 });
      let top = 330;
      if (im) { photo(p, { x: (W - 400) / 2, y: 280, w: 400, h: 440 }, true); top = 800; }
      text(p, W / 2, top, c.kicker, 'bodyHeavy', 32, C.turquesa, 'middle', 'kicker');
      const t = title(p, c.title, { x: W / 2, top: top + 40, w: W - 2 * M, max: im ? 104 : 150, min: im ? 88 : 110, maxLines: 3, color: C.tiza, anchor: 'middle', mark: { kind: 'block', color: C.coral }, phrase: c.highlight });
      let y = t.bottom + (im ? 60 : 90);
      line(p, `M${M} ${y} L${W - M} ${y - 4}`, C.tizaSoft, 2.4);
      y += im ? 90 : 110;
      c.points.forEach((r, i) => {
        if (!r) return;
        const ls = wrap(r, 'bodyBold', im ? 44 : 48, W - 2 * M);
        ls.forEach((l, k) => { const w = text(p, W / 2, y + k * 56, l, 'bodyBold', im ? 44 : 48, C.tiza, 'middle', `points.${i}`); if (k === ls.length - 1) strokes(p, S.underline(W / 2 - w / 2, y + k * 56 + 20, W / 2 + w / 2, y + k * 56 + 16, 650 + i, { passes: 1 }), ACC[i], 5); });
        y += (ls.length - 1) * 56 + (im ? 96 : 130);
      });
      para(p, c.note, M, 1500, W - 2 * M, 'note', 60, 64, C.amarillo, 'note', 'middle');
      strokes(p, S.arrow(W / 2, 1530, W / 2 + 10, 1650, 61, { bend: 12, head: 16 }), C.amarillo, 5);
    }
  }

  if (key === 'pz_li_post') {
    const M = 72, c = content;
    if (!B) {
      const p = page(C.papel, { bottom: H - 100 });
      const sw = 360;
      if (im) photo(p, { x: 0, y: 0, w: sw, h: H }, false, C.panel);
      const x = im ? sw + 56 : M, w = im ? W - x - M : W - 2 * M;
      text(p, x, 130, c.kicker, 'bodyHeavy', 32, C.turquesaTexto, 'start', 'kicker');
      const t = title(p, c.title, { x, top: 160, w, max: im ? 104 : 132, min: im ? 84 : 100, maxLines: 3, color: C.petroleo, mark: { kind: 'block', color: C.amarillo }, phrase: c.highlight });
      const y = para(p, c.body, x, t.bottom + 64, im ? w : 780, 'body', 38, 52, C.texto, 'body');
      const sy = im ? y + 140 : Math.max(y + 140, H - 230), gap = w / 3;
      c.points.forEach((l, i) => {
        const cx = x + gap * i + gap / 2;
        text(p, cx, sy, l, 'note', im ? 48 : 56, [C.gris, C.coralTexto, C.turquesaTexto][i], 'middle', `points.${i}`);
        if (i === 1 && l) strokes(p, S.loop(cx, sy - 16, Math.max(tw(l, 'note', im ? 48 : 56) / 2 + 22, 60), im ? 42 : 48, 101), C.coral, 4);
        if (i < 2) strokes(p, S.arrow(cx + gap * 0.28, sy - 14, cx + gap * 0.62, sy - 14, 111 + i, { bend: -8, head: 11 }), C.petroleo, 3.4);
      });
      sig(p, x, H - 56, C.petroleo, 32);
    } else {
      const p = page(C.papel, { bottom: H - 96 });
      const band = H > 1080 ? 540 : 450;
      rect(p, 0, 0, W, band, C.petroleo); tex(p, 0, 0, W, band);
      text(p, M, 110, c.kicker, 'bodyHeavy', 32, C.amarillo, 'start', 'kicker');
      const t = title(p, c.title, { x: M, top: 136, w: W - 2 * M, max: 124, min: 92, maxLines: 2, maxBottom: band - 10, color: C.tiza, mark: { kind: 'underline', color: C.amarillo, width: 9 }, phrase: c.highlight });
      if (t.bottom > band - 10) issues.push({ code: 'overflow', field: 'title', page: p.n });
      const pw = 320, colW = im ? W - 2 * M - pw - 40 : W - 2 * M;
      let y = para(p, c.body, M, band + 90, colW, 'body', 38, 52, C.texto, 'body') + 100;
      if (im) {
        photo(p, { x: W - M - pw, y: band + 40, w: pw, h: H - band - 150 }, false);
        c.points.forEach((l, i) => { if (!l) return; strokes(p, S.loop(M + 26, y - 12, 26, 26, 600 + i), ACC[i], 4); text(p, M + 26, y + 6, String(i + 1), 'title', 50, C.petroleo, 'middle'); y = para(p, l, M + 76, y, colW - 76, 'bodyBold', 38, 46, C.tinta, `points.${i}`) + 74; });
      } else {
        const gap = (W - 2 * M) / 3;
        c.points.forEach((l, i) => {
          const cx = M + gap * i + gap / 2;
          strokes(p, S.loop(cx, y + 10, 46, 46, 600 + i), ACC[i], 5);
          text(p, cx, y + 32, String(i + 1), 'title', 64, C.petroleo, 'middle');
          para(p, l, cx - gap / 2 + 10, y + 130, gap - 20, 'note', 56, 56, ACC_T[i], `points.${i}`, 'middle');
          if (i < 2) strokes(p, S.arrow(cx + 64, y + 8, cx + gap - 64, y + 8, 610 + i, { bend: -10, head: 12 }), C.petroleo, 3.4);
        });
      }
      sig(p, M, H - 48, C.petroleo, 32);
    }
  }

  if (key === 'pz_ig_carousel') {
    const M = 80, c = content;
    // cover
    if (!B) {
      const p = page(C.carbon, { dark: true, bottom: H - 150, label: 'Portada' });
      text(p, W / 2, 230, c.cover.kicker, 'bodyHeavy', 32, C.turquesa, 'middle', 'cover.kicker');
      const t = title(p, c.cover.title, { x: W / 2, top: 290, w: W - 2 * M, max: im ? 140 : 176, min: 112, maxLines: 3, color: C.tiza, anchor: 'middle', mark: { kind: 'loop', color: C.amarillo, width: 7 }, phrase: c.cover.highlight, f: 'cover.title' });
      const by = para(p, c.cover.body, 160, t.bottom + (im ? 60 : 90), 760, 'body', 42, 58, C.tizaSoft, 'cover.body', 'middle');
      if (im) photo(p, { x: 290, y: by + 50, w: 500, h: Math.max(160, H - by - 230) }, true);
      text(p, W - M - 140, H - 100, 'Deslizá', 'note', 58, C.amarillo, 'end', null, true);
      strokes(p, S.arrow(W - M - 124, H - 118, W - M, H - 122, 31, { bend: -6, head: 16 }), C.amarillo, 5);
    } else {
      const split = 760;
      const p = page(C.papel, { bottom: H - 150, label: 'Portada' });
      rect(p, 0, 0, W, split, C.petroleo); tex(p, 0, 0, W, split);
      text(p, M, 150, c.cover.kicker, 'bodyHeavy', 32, C.amarillo, 'start', 'cover.kicker');
      const t = title(p, c.cover.title, { x: M, top: 200, w: W - 2 * M, max: 168, min: 112, maxLines: 3, maxBottom: split - 90, color: C.tiza, mark: { kind: 'block', color: C.coral }, phrase: c.cover.highlight, f: 'cover.title' });
      if (t.bottom > split - 90) issues.push({ code: 'overflow', field: 'cover.title', page: p.n });
      const pw = 380;
      para(p, c.cover.body, M, split + 110, im ? W - 2 * M - pw - 30 : W - 2 * M - 160, 'body', 44, 60, C.texto, 'cover.body');
      if (im) photo(p, { x: W - M - pw + 40, y: split + 30, w: pw - 40, h: H - split - 60 }, false);
      strokes(p, S.arrow(M + 40, split - 70, M + 10, split + 60, 81, { bend: 14, head: 14 }), C.amarillo, 5);
      const dw = text(p, M, H - 80, 'Deslizá', 'note', 56, C.coralTexto, 'start', null, true);
      strokes(p, S.arrow(M + dw + 16, H - 98, M + dw + 120, H - 102, 82, { bend: -6, head: 15 }), C.coral, 4.5);
    }
    // content slides: a slide whose text does not fit continues on the next one
    const BODY = 46, LH = 64;
    c.slides.forEach((s, i) => {
      const accent = i % 3, f = `slides.${i}`;
      const mk = (cont) => {
        const p = page(C.papel, { bottom: H - 130, label: `Paso ${i + 1}${cont ? ' (sigue)' : ''}` });
        let x, w, y;
        if (!B) {
          text(p, M, 140, `PASO ${i + 1}`, 'bodyHeavy', 32, ACC_T[accent]);
          if (cont) { text(p, M, 220, `${s.title} (sigue)`, 'bodyBold', 36, C.gris); x = M; w = W - 2 * M; y = 320; }
          else {
            const num = String(i + 1);
            text(p, M + 10, 420, num, 'title', 300, ACC[accent], 'start');
            strokes(p, S.loop(M + 80 + (num.length - 1) * 60, 300, 120 + (num.length - 1) * 60, 150, 50 + i), ACC[accent], 6);
            const nx = num.length > 1 ? 440 : 340;
            const tt = title(p, s.title, { x: nx, top: 220, w: W - nx - M, max: 96, min: 76, maxLines: 4, color: C.petroleo, f: `${f}.title` });
            x = M; w = W - 2 * M; y = Math.max(tt.bottom, 470) + 80;
          }
        } else {
          rect(p, 0, 0, 200, H, C.panel);
          text(p, 100, 300, String(i + 1), 'title', 220, ACC[accent], 'middle');
          strokes(p, S.underline(50, 340, 150, 336, 90 + i), ACC[accent], 6);
          text(p, 100, 404, 'PASO', 'bodyHeavy', 32, C.gris, 'middle');
          x = 250; w = W - x - M;
          if (cont) { text(p, x, 170, `${s.title} (sigue)`, 'bodyBold', 36, C.gris); y = 270; }
          else { const tt = title(p, s.title, { x, top: 140, w, max: 100, min: 80, maxLines: 4, color: C.petroleo, f: `${f}.title` }); y = tt.bottom + 70; }
        }
        return { p, x, w, y };
      };
      let cur = mk(false);
      const lines = wrap(s.body, 'body', BODY, cur.w);
      let k = 0;
      while (k < lines.length) {
        const room = Math.floor((cur.p.bottom - (cur.y + BODY * 0.22)) / LH) + 1;
        const take = lines.slice(k, k + Math.max(room, 0));
        if (!take.length) { cur = mk(true); continue; }
        // keep at least two lines on each side of a break (never a lone line)
        const rest = lines.length - (k + take.length);
        const n = rest === 1 && take.length > 2 ? take.length - 1 : take.length;
        lines.slice(k, k + n).forEach((l, j) => text(cur.p, cur.x, cur.y + j * LH, l, 'body', BODY, C.texto, 'start', `${f}.body`));
        cur.y += n * LH; k += n;
        if (k < lines.length) cur = mk(true);
      }
      if (s.note) {
        const ny = cur.y + 90;
        if (!B) {
          const nw = tw(s.note, 'note', 56);
          text(cur.p, W - M, ny + 30, s.note, 'note', 56, C.coralTexto, 'end', `${f}.note`);
          strokes(cur.p, S.arrow(W - M - nw - 20, ny + 10, W - M - nw - 120, ny - 70, 70 + i, { bend: 10, head: 13 }), C.coral, 4);
        } else para(cur.p, s.note, cur.x, ny, cur.w, 'note', 56, 60, C.coralTexto, `${f}.note`);
      }
    });
    // closing
    {
      const p = page(C.papel, { bottom: H - 150, label: 'Cierre' });
      const x = B ? 250 : M;
      if (B) { rect(p, 0, 0, 200, H, C.panel); strokes(p, S.loop(100, 300, 56, 56, 880), C.turquesa, 5); check(p, 100, 300, C.petroleo); }
      const t = title(p, c.closing.title, { x, top: 220, w: W - x - M, max: 128, min: 96, maxLines: 4, color: C.petroleo, mark: { kind: 'underline', color: C.coral, width: 10 }, phrase: c.closing.highlight, f: 'closing.title' });
      para(p, c.closing.body, x, t.bottom + 110, Math.min(820, W - x - M), 'body', 44, 62, C.texto, 'closing.body');
      sig(p, x, H - 170, C.petroleo, 32);
      strokes(p, S.underline(x, H - 146, x + tw(AUTHOR.toUpperCase(), 'bodyHeavy', 32) + 10, H - 150, 891, { passes: 1 }), C.turquesa, 4);
    }
    const N = pages.length;
    pages.forEach((p, i) => { if (i) { foot(p, B ? 250 : M, H - 60, AUTHOR, C.gris); foot(p, W - M, H - 60, `${i + 1}/${N}`, C.gris, 'end'); } });
  }

  if (key === 'pz_li_document') {
    const M = 88, c = content, X = B ? 290 : M, TW = W - X - M;
    // cover (its page count is filled in once the pages are laid out)
    const cv = page(B ? C.carbon : C.papel, { dark: B, bottom: H - 230, label: 'Portada' });
    if (!B) rect(cv, 0, 0, W, 26, C.turquesa);
    text(cv, M, 170, c.cover.kicker, 'bodyHeavy', 32, B ? C.turquesa : C.coralTexto, 'start', 'cover.kicker');
    const t = title(cv, c.cover.title, { x: M, top: 220, w: W - 2 * M, max: im ? 112 : (B ? 144 : 134), min: 96, maxLines: 3, color: B ? C.tiza : C.petroleo, mark: { kind: B ? 'underline' : 'block', color: C.amarillo, width: 9 }, phrase: c.cover.highlight, f: 'cover.title' });
    if (im) {
      if (B) { const by = para(cv, c.cover.body, M, t.bottom + 70, W - 2 * M, 'body', 40, 56, C.tizaSoft, 'cover.body'); photo(cv, { x: M, y: by + 50, w: W - 2 * M, h: Math.max(160, H - by - 300) }, true); }
      else { para(cv, c.cover.body, M, t.bottom + 80, 470, 'body', 40, 56, C.texto, 'cover.body'); photo(cv, { x: 600, y: t.bottom + 30, w: W - 600 - 50, h: Math.max(160, H - t.bottom - 260) }, false); }
    } else para(cv, c.cover.body, M, t.bottom + 90, 840, 'body', 44, 62, B ? C.tizaSoft : C.texto, 'cover.body');
    const metaAt = cv.items.length;
    sig(cv, M, H - 90, B ? C.tiza : C.petroleo, 32);
    // reading flow: a block that does not fit starts the next page (never shrunk); a long paragraph splits by lines
    const BODY = 44, LH = 62;
    let p = null, y = 0, sec = null, secN = 0;
    const newPage = (cont) => {
      p = page(C.papel, { bottom: H - 150, label: `${PZ_SECTION_TYPES[sec.type].label}${cont ? ' (sigue)' : ''}` });
      y = 150;
      if (B) { rect(p, 0, 0, X - 40, H, C.panel); text(p, (X - 40) / 2, 260, String(secN), 'title', 180, C.turquesa, 'middle'); text(p, (X - 40) / 2, 324, sec.type === 'questions' ? 'PREGUNTAS' : 'LECTURA', 'bodyHeavy', 32, C.gris, 'middle'); }
      if (cont) { text(p, X, y - 20, `${sec.title} (sigue)`, 'bodyBold', 34, C.gris); y += 50; }
    };
    const fits = (h) => y + h <= p.bottom;
    c.sections.forEach((s, si) => {
      sec = s; secN = si + 1;
      newPage(false);
      const tt = title(p, s.title, { x: X, top: y - 30, w: TW, max: 92, min: 76, maxLines: 3, color: C.petroleo, mark: { kind: B ? 'block' : 'underline', color: B ? C.amarillo : C.turquesa, width: 7 }, phrase: s.highlight, f: `sections.${si}.title` });
      y = tt.bottom + 90;
      if (s.type === 'reading') {
        s.paras.forEach((t0, pi) => {
          const ls = wrap(t0, 'body', BODY, TW);
          let k = 0;
          while (k < ls.length) {
            const room = Math.floor((p.bottom - y) / LH);
            if (room < 2 && ls.length - k >= 2) { newPage(true); continue; }
            let n = Math.min(room, ls.length - k);
            if (ls.length - (k + n) === 1 && n > 2) n -= 1;
            if (n <= 0) { newPage(true); continue; }
            ls.slice(k, k + n).forEach((l, j) => text(p, X, y + j * LH, l, 'body', BODY, C.texto, 'start', `sections.${si}.paras.${pi}`));
            y += n * LH; k += n;
            if (k < ls.length) newPage(true);
          }
          y += 34;
        });
        if (s.quote) {
          if (B) {
            // in «Ficha» the quote is a margin note beside the text it sums up
            const ql = nLines(s.quote, 'note', 40, X - 90);
            para(p, s.quote, 24, Math.max(380, y - ql * 44 - 40), X - 90, 'note', 40, 44, C.coralTexto, `sections.${si}.quote`);
          } else {
            const ql = nLines(s.quote, 'note', 64, TW - 50);
            if (!fits(ql * 70 + 80)) newPage(true);
            line(p, `M${X + 4} ${y + 20} L${X + 2} ${y + 40 + ql * 70}`, C.coral, 8);
            y = para(p, s.quote, X + 44, y + 80, TW - 50, 'note', 64, 70, C.coralTexto, `sections.${si}.quote`) + 100;
          }
        }
      } else {
        s.items.forEach((it, i) => {
          const ix = X + (B ? 50 : 110), iw = W - ix - M;
          const h = nLines(it.q, 'bodyHeavy', 46, iw) * 56 + 8 + nLines(it.a, 'body', 40, iw) * 56 + 60;
          if (!fits(h)) newPage(true);
          if (B) line(p, S.dash(X, y - 14, 30), ACC[i % 3], 7);
          else { strokes(p, S.loop(X + 40, y - 18, 38, 38, 300 + i), ACC[i % 3], 5); text(p, X + 40, y + 4, String(i + 1), 'title', 58, C.petroleo, 'middle'); }
          y = para(p, it.q, ix, y, iw, 'bodyHeavy', 46, 56, C.tinta, `sections.${si}.items.${i}.q`) + 64;
          y = para(p, it.a, ix, y, iw, 'body', 40, 56, C.texto, `sections.${si}.items.${i}.a`) + 130;
        });
      }
    });
    const N = pages.length;
    // the cover says how many pages there are, computed from the layout (never typed)
    const meta = `${N} página${N === 1 ? '' : 's'}${c.cover.note ? ` · ${c.cover.note}` : ''}`;
    const metaItem = { t: 'text', x: M, y: H - 190, text: meta, font: 'note', size: 54, color: B ? C.amarillo : C.turquesaTexto, w: tw(meta, 'note', 54), f: 'cover.note', foot: true };
    cv.items.splice(metaAt, 0, metaItem);
    // the footer: author and title, shortened with «…» so it never reaches the page number
    const footerText = (num) => {
      const room = TW - tw(num, 'bodyBold', 34) - 40;
      let t = `${AUTHOR} · ${String(c.cover.title || '').replace(/\s+/g, ' ').trim()}`;
      if (!c.cover.title) t = AUTHOR;
      if (tw(t, 'bodyBold', 34) <= room) return t;
      // drop whole words (never half a word) and mark the cut
      const ws = t.split(' ');
      while (ws.length > 3 && tw(`${ws.join(' ')}…`, 'bodyBold', 34) > room) ws.pop();
      return ws.length > 3 ? `${ws.join(' ').replace(/[\s·,;:]+$/, '')}…` : AUTHOR;
    };
    pages.forEach((pg, i) => { if (i) { const num = `${i + 1}/${N}`; pg.items.push({ t: 'path', d: `M${X} ${H - 112} L${W - M} ${H - 114}`, stroke: C.linea, width: 2, opacity: 1 }); foot(pg, X, H - 60, footerText(num), C.gris); foot(pg, W - M, H - 60, num, C.gris, 'end'); } });
  }

  if (key === 'pz_youtube') {
    const M = 64, c = content;
    const duration = { kind: 'cover', x: 1080, y: 630, w: 200, h: 90, label: 'Duración del video' };
    if (!B) {
      const p = page(C.carbon, { dark: true, bottom: H - 40 });
      p.guides = [duration];
      if (im) photo(p, { x: 760, y: 0, w: W - 760, h: H }, true);
      const w = im ? 760 - M - 30 : W - 2 * M - 260;
      if (c.kicker) text(p, M, 108, c.kicker, 'bodyHeavy', 44, C.turquesa, 'start', 'kicker');
      title(p, c.title, { x: M, top: c.kicker ? 150 : 96, w, max: im ? 150 : 176, min: 104, maxLines: 3, maxBottom: H - 70, color: C.tiza, mark: { kind: 'block', color: C.turquesa, sparks: C.amarillo }, phrase: c.highlight });
      if (!im) {
        // a staircase that rises: the idea at a glance, away from the duration corner
        const sx = W - M - 300, base = 470, st = 96, sh = 90;
        line(p, `M${sx} ${base} L${sx + st} ${base} L${sx + st} ${base - sh} L${sx + 2 * st} ${base - sh} L${sx + 2 * st} ${base - 2 * sh} L${sx + 3 * st} ${base - 2 * sh}`, C.tiza, 7);
        strokes(p, S.loop(sx + st / 2, base - 26, 58, 30, 9), C.amarillo, 6);
        strokes(p, S.arrow(sx + 3 * st - 10, base - 2 * sh - 24, sx + 3 * st + 46, base - 2 * sh - 90, 5, { bend: -8, head: 18 }), C.coral, 6);
      }
    } else {
      const p = page(C.papel, { bottom: H - 40 });
      p.guides = [duration];
      let x0 = M, w = W - 2 * M;
      if (im) { photo(p, { x: 0, y: 0, w: 470, h: H }, false, C.panel); x0 = 520; w = W - 520 - M; }
      const cx = x0 + w / 2;
      if (c.kicker) text(p, cx, 104, c.kicker, 'bodyHeavy', 44, C.coralTexto, 'middle', 'kicker');
      const t = title(p, c.title, { x: cx, top: c.kicker ? 140 : 100, w, max: im ? 140 : 168, min: 96, maxLines: 3, maxBottom: 500, color: C.petroleo, anchor: 'middle', mark: { kind: 'underline', color: C.coral, width: 13 }, phrase: c.highlight });
      const sy = Math.max(t.bottom + 110, 580), gap = Math.min(w / 3, 300);
      if (sy < H - 40) [0, 1, 2].forEach(i => {
        const px = cx + (i - 1) * gap;
        strokes(p, S.loop(px, sy - 28, 46, 46, 70 + i), ACC[i], 7);
        text(p, px, sy, String(i + 1), 'title', 72, C.petroleo, 'middle');
        if (i < 2) strokes(p, S.arrow(px + 62, sy - 30, px + gap - 62, sy - 30, 80 + i, { bend: -8, head: 16 }), C.petroleo, 5);
      });
    }
  }

  if (key === 'pz_tiktok_cover') {
    const M = 80, c = content;
    const ct = Number.isFinite(c.cropTop) ? c.cropTop : tpl.crop.top;
    const crop = { kind: 'crop', x: 0, y: ct, w: W, h: tpl.crop.h, label: 'Recorte del perfil (orientativo, 3:4)' };
    const views = { kind: 'cover', x: 0, y: ct + tpl.crop.h - 110, w: 300, h: 110, label: 'Vistas' };
    const bottom = ct + tpl.crop.h - 120;
    if (!B) {
      const p = page(C.petroleo, { dark: true, bottom });
      p.guides = [crop, views];
      const t = title(p, c.title, { x: M, top: ct + 90, w: W - 2 * M, max: im ? 190 : 230, min: 140, maxLines: 4, maxBottom: im ? ct + 780 : ct + 1080, color: C.tiza, mark: { kind: 'block', color: C.coral }, phrase: c.highlight });
      if (im) photo(p, { x: 300, y: t.bottom + 60, w: W - 300 - 60, h: Math.max(240, ct + tpl.crop.h - t.bottom - 100) }, true);
      else {
        const y = Math.min(t.bottom + 200, ct + 1260);
        [0, 1, 2].forEach(i => { const x = 230 + i * 310; strokes(p, S.loop(x, y, 92, 92, 30 + i), ACC[i], 11); text(p, x, y + 46, String(i + 1), 'title', 140, C.tiza, 'middle'); if (i < 2) strokes(p, S.arrow(x + 112, y, x + 196, y, 40 + i, { bend: -10, head: 22 }), C.tizaSoft, 8); });
      }
    } else {
      const p = page(C.papel, { bottom });
      p.guides = [crop, views];
      let top = ct + 330;
      if (im) { photo(p, { x: 160, y: ct + 40, w: W - 320, h: 600 }, false, C.panel); top = ct + 700; }
      const t = title(p, c.title, { x: W / 2, top, w: W - 2 * M, max: im ? 180 : 230, min: 140, maxLines: 4, maxBottom: bottom, color: C.petroleo, anchor: 'middle', mark: { kind: 'loop', color: C.coral, width: 11 }, phrase: c.highlight });
      if (!im && t.bottom + 100 < bottom) strokes(p, S.underline(W / 2 - 220, t.bottom + 90, W / 2 + 220, t.bottom + 84, 61), C.turquesa, 12);
    }
  }

  // ── Checks: a text longer than its field; text past the bottom margin or inside the photo zone (named, never cut) ──
  issues.unshift(...pizarraTooLong(key, content).map(i => ({ ...i, page: null })));
  for (const p of pages) {
    for (const it of p.items) {
      if (it.t !== 'text' || !it.f) continue;
      const top = it.y - it.size * 0.8, bot = it.y + it.size * 0.22;
      if (!it.foot && bot > p.bottom + 0.5 && !issues.some(i => i.code === 'overflow' && i.field === it.f)) issues.push({ code: 'overflow', field: it.f, page: p.n });
      // a character the font does not have (an emoji, an arrow) would come out as an empty box in the PNG or the PDF
      if (measure.covers && !measure.covers(it.text, it.font) && !issues.some(i => i.code === 'glyph' && i.field === it.f)) issues.push({ code: 'glyph', field: it.f, page: p.n });
      for (const g of p.guides || []) {
        const inside = it.x < g.x + g.w && it.x + it.w > g.x && top < g.y + g.h && bot > g.y;
        if (g.kind === 'cover' && inside && !issues.some(i => i.code === 'ui' && i.field === it.f)) issues.push({ code: 'ui', field: it.f, page: p.n, zone: g.label });
        if (g.kind === 'crop' && (top < g.y || bot > g.y + g.h) && !issues.some(i => i.code === 'crop' && i.field === it.f)) issues.push({ code: 'crop', field: it.f, page: p.n });
      }
      for (const z of p.zones) if (it.x < z.x + z.w && it.x + it.w > z.x && top < z.y + z.h && bot > z.y && !issues.some(i => i.code === 'overlap' && i.field === it.f)) issues.push({ code: 'overlap', field: it.f, page: p.n });
    }
  }
  return { size: { w: W, h: H }, pages, issues, view: tpl.view };
}

/** The smallest text of a layout as seen on a phone (CSS px), and the titles' (for the tests). */
export function shownSizes(layout) {
  const k = layout.view / layout.size.w;
  const texts = layout.pages.flatMap(p => p.items.filter(i => i.t === 'text'));
  return { text: Math.min(...texts.filter(i => i.font !== 'title').map(i => i.size)) * k, title: Math.min(...texts.filter(i => i.font === 'title').map(i => i.size)) * k };
}

/** The text that goes with the piece (caption) to copy, or a short default from its texts. */
export const pizarraHeadline = (key, c) => String((PZ_TEMPLATES[key]?.kind === 'single' ? c?.title : c?.cover?.title) || '').slice(0, 140);

// ── The weekly plan (agreed with Ale on 04-11-2026): new pieces of Instagram and LinkedIn are Pizarra pieces ──
/** The fields the AI drafts for a plan's piece (same names as the content; the code places them). */
export const PZ_AI = Object.freeze({
  pz_li_post: {
    slots: [
      { key: 'kicker', label: 'Antetítulo: 2 a 4 palabras que sitúan el tema', max: 32 },
      { key: 'title', label: 'Título: una idea o pregunta corta', max: 64 },
      { key: 'highlight', label: 'Palabra destacada: una o dos palabras copiadas del título', max: 24 },
      { key: 'body', label: 'Texto: una o dos frases que desarrollan el título', max: 200 },
      { key: 'point1', label: 'Esquema, primera palabra (una o dos palabras)', max: 18 },
      { key: 'point2', label: 'Esquema, segunda palabra (la acción central)', max: 18 },
      { key: 'point3', label: 'Esquema, tercera palabra (a dónde lleva)', max: 18 },
    ],
  },
  pz_ig_carousel: {
    slides: 3,
    slots: [
      { key: 'cover_kicker', label: 'Portada · antetítulo (2 a 4 palabras)', max: 32 },
      { key: 'cover_title', label: 'Portada · título', max: 60 },
      { key: 'cover_highlight', label: 'Portada · palabra destacada, copiada del título', max: 24 },
      { key: 'cover_body', label: 'Portada · una frase que promete qué se aprende', max: 160 },
      { key: 'closing_title', label: 'Cierre · título (la idea para llevarse)', max: 60 },
      { key: 'closing_highlight', label: 'Cierre · palabra destacada, copiada del título del cierre', max: 24 },
      { key: 'closing_body', label: 'Cierre · una frase con la acción concreta o la llamada a la acción', max: 200 },
    ],
    slideSlots: [
      { key: 'title', label: 'Título del paso', max: 60 },
      { key: 'body', label: 'Texto del paso (frases cortas)', max: 360 },
      { key: 'note', label: 'Anotación breve a mano (opcional)', max: 40 },
    ],
  },
});
/** The Pizarra template a plan's format uses for a new piece (TikTok stays a script; any other format, unchanged). */
export const PLAN_PIZARRA = Object.freeze({ li_square: 'pz_li_post', li_post: 'pz_li_post', ig_carousel: 'pz_ig_carousel' });

const fp = (str) => { let h = 0x811c9dc5; for (const ch of str) { h ^= ch.charCodeAt(0); h = Math.imul(h, 0x01000193) >>> 0; } return h.toString(36); };
/** Every text of a piece in order (and its caption): what «edited» compares. */
export function pizarraTexts(key, c) {
  const kind = PZ_TEMPLATES[key]?.kind;
  if (kind === 'single') return [c.kicker, c.title, c.highlight, c.body, ...(c.points || []), c.note].map(x => String(x || ''));
  if (kind === 'carousel') return [c.cover?.kicker, c.cover?.title, c.cover?.highlight, c.cover?.body, ...(c.slides || []).flatMap(s => [s.title, s.body, s.note]), c.closing?.title, c.closing?.highlight, c.closing?.body].map(x => String(x || ''));
  return [c.cover?.kicker, c.cover?.title, c.cover?.highlight, c.cover?.body, c.cover?.note, ...(c.sections || []).flatMap(s => [s.title, s.highlight, ...(s.paras || []), s.quote, ...(s.items || []).flatMap(i => [i.q, i.a])])].map(x => String(x || ''));
}
export const pizarraTextsFp = (key, c, caption = '') => fp(JSON.stringify([...pizarraTexts(key, c), String(caption || '')]));

/**
 * The content of a plan's piece from the AI proposal (checked by the code before): each text whole in its field (the
 * whole proposal also stays in the piece's «AI Draft»). Without a proposal
 * («Escribirlo yo») the texts start empty. Composition A, no photo.
 */
export function pizarraFromProposal(key, proposal = null) {
  const c = { identity: { key: IDENTITY_A.key, version: IDENTITY_A.version }, layout: 'A', image: null };
  // Whole texts: a text longer than its field is named in the editor (to edit or ask for a shorter version), never cut
  const t = (v) => String(v?.text ?? v ?? '').replace(/\r/g, '').trim();
  const sl = proposal?.slots || {};
  if (key === 'pz_li_post') {
    Object.assign(c, { kicker: t(sl.kicker), title: t(sl.title), highlight: t(sl.highlight), body: t(sl.body), points: [t(sl.point1), t(sl.point2), t(sl.point3)] });
  } else if (key === 'pz_ig_carousel') {
    const list = Array.isArray(proposal?.slides) ? proposal.slides : [];
    const slides = list.map(x => ({ title: t(x?.title), body: t(x?.body), note: t(x?.note) })).filter(x => x.title || x.body || x.note);
    Object.assign(c, {
      cover: { kicker: t(sl.cover_kicker), title: t(sl.cover_title), highlight: t(sl.cover_highlight), body: t(sl.cover_body) },
      slides: (slides.length ? slides : [{ title: '', body: '', note: '' }]).slice(0, L.slides.max),
      closing: { title: t(sl.closing_title), highlight: t(sl.closing_highlight), body: t(sl.closing_body) },
    });
  } else throw new Error('template');
  return c;
}

/** Whether Ale already touched a plan's Pizarra piece: approved, or texts (or caption) different from what the AI proposed. */
export function pizarraEdited(piece) {
  if (!piece) return false;
  if (piece.status === 'approved') return true;
  const c = piece.content || {};
  if (c.aiFp) return pizarraTextsFp(piece.template, c, piece.caption) !== c.aiFp;
  return pizarraTexts(piece.template, c).some(x => x.trim()) || !!String(piece.caption || '').trim();
}

/** A short text preview of a Pizarra piece, to review the proposal in the plan without opening the editor. */
export function pizarraPreview(piece) {
  const c = piece?.content || {}; const out = [];
  const add = (key, label, text) => { const v = String(text || '').trim(); if (v) out.push({ key, label, text: v.slice(0, 900) }); };
  if (PZ_TEMPLATES[piece?.template]?.kind === 'single') {
    add('kicker', 'Antetítulo', c.kicker); add('title', 'Título', c.title); add('body', 'Texto', c.body);
    add('points', 'Tres puntos', (c.points || []).filter(Boolean).join(' · ')); add('note', 'Anotación', c.note);
  } else if (PZ_TEMPLATES[piece?.template]?.kind === 'carousel') {
    add('cover', 'Portada', [c.cover?.title, c.cover?.body].filter(Boolean).join(' · '));
    (c.slides || []).forEach((s, i) => add(`slide${i}`, `Paso ${i + 1}`, [s.title, s.body, s.note].filter(Boolean).join(' · ')));
    add('closing', 'Cierre', [c.closing?.title, c.closing?.body].filter(Boolean).join(' · '));
  }
  add('caption', 'Texto que acompaña', piece?.caption);
  return out;
}
